"use client";
import Plus from "@/assets/svg/16/Plus";
import Minus from "@/assets/svg/Minus";
import clsx from "clsx";
import Image, { ImageProps, getImageProps } from "next/image";
import React, { useState } from "react";
import styles from "./styles.module.css";

/**
 * Extension of Next Image with fill that allows zooming in on the image
 */
const ZoomImage = ({ src, className, alt, fill: _, ...rest }: ImageProps) => {
  const fill = true;
  const [zoom, setZoom] = useState(false);
  const {
    props: { src: bgSrc }
  } = getImageProps({ alt, src, fill, sizes: "100vw", priority: false });

  const zoomOut = () => {
    setZoom(false);
  };

  const zoomIn = () => {
    setZoom(true);
  };

  const handleZoomMove = (
    e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>
  ) => {
    if (!zoom) return;
    const zoomer = e.currentTarget;

    // Fix for React event handling
    if ((e.target as HTMLElement).tagName !== "FIGURE") return;

    let offsetX: number;
    let offsetY: number;

    if (e.nativeEvent instanceof MouseEvent) {
      offsetX = e.nativeEvent.offsetX;
      offsetY = e.nativeEvent.offsetY;
    } else if (
      e.nativeEvent instanceof TouchEvent &&
      e.nativeEvent.touches.length > 0
    ) {
      offsetX = e.nativeEvent.touches[0].pageX - zoomer.offsetLeft;
      offsetY = e.nativeEvent.touches[0].pageY - zoomer.offsetTop;
    } else {
      return;
    }

    const x = (offsetX / zoomer.offsetWidth) * 100;
    const y = (offsetY / zoomer.offsetHeight) * 100;

    zoomer.style.backgroundPosition = `${x}% ${y}%`;
  };

  return (
    <figure
      onMouseMove={zoom ? handleZoomMove : undefined}
      onTouchMove={zoom ? handleZoomMove : undefined}
      onMouseLeave={zoomOut}
      onClick={zoom ? zoomOut : undefined}
      style={{ backgroundImage: bgSrc ? `url(${bgSrc})` : undefined }}
      className={clsx(styles.zoomImageContainer, zoom && styles.zoom)}
    >
      <Image
        src={src}
        fill={fill}
        className={clsx(styles.image, className)}
        alt={alt}
        {...rest}
      />
      <button
        aria-hidden
        onClick={zoom ? zoomOut : zoomIn}
        className={styles.zoomButton}
      >
        {zoom ? <Minus color="currentColor" /> : <Plus />}
      </button>
    </figure>
  );
};

export default ZoomImage;
